// Drawing and shape utilities for PdfTableViewer

/**
 * Start drawing a shape on the PDF
 */
export const startDrawing = (
  event,
  {
    drawingMode,
    isResizing,
    isDraggingGridLine,
    getCanvasCoordinates,
    convertCanvasToPdfCoordinates,
    canvasRef,
    highlightContainerRef,
    pdfPageDimensions,
    setIsDrawing,
    setDrawingStartPoint,
    setPolygonPoints,
    setDrawingShape,
  }
) => {
  if (!drawingMode || isResizing || isDraggingGridLine) {
    return;
  }

  const coords = getCanvasCoordinates(event, canvasRef, highlightContainerRef);
  if (!coords) return;

  const pdfCoords = convertCanvasToPdfCoordinates(
    coords.x,
    coords.y,
    canvasRef,
    pdfPageDimensions
  );
  if (!pdfCoords) return;
  setIsDrawing(true);
  setDrawingStartPoint(pdfCoords);

  if (drawingMode === "polygon") {
    // For polygon, add points on each click
    setPolygonPoints((prev) => [...prev, pdfCoords]);
  } else if (drawingMode === "freehand") {
    // For freehand, start with current point
    setDrawingShape({
      type: "freehand",
      points: [pdfCoords],
    });
  }
};

/**
 * Continue drawing based on mouse movement
 */
export const continueDrawing = (
  event,
  {
    isDrawing,
    drawingStartPoint,
    drawingMode,
    isDraggingGridLine,
    getCanvasCoordinates,
    convertCanvasToPdfCoordinates,
    canvasRef,
    highlightContainerRef,
    pdfPageDimensions,
    setDrawingShape,
  }
) => {
  if (!isDrawing || !drawingStartPoint || !drawingMode || isDraggingGridLine)
    return;

  const coords = getCanvasCoordinates(event, canvasRef, highlightContainerRef);
  if (!coords) return;

  const pdfCoords = convertCanvasToPdfCoordinates(
    coords.x,
    coords.y,
    canvasRef,
    pdfPageDimensions
  );
  if (!pdfCoords) return;

  switch (drawingMode) {
    case "rectangle": {
      const width = pdfCoords.x - drawingStartPoint.x;
      const height = pdfCoords.y - drawingStartPoint.y;
      setDrawingShape({
        type: "rectangle",
        x: drawingStartPoint.x,
        y: drawingStartPoint.y,
        width,
        height,
      });
      break;
    }
    case "circle": {
      const radius = Math.sqrt(
        (pdfCoords.x - drawingStartPoint.x) ** 2 +
          (pdfCoords.y - drawingStartPoint.y) ** 2
      );
      setDrawingShape({
        type: "circle",
        centerX: drawingStartPoint.x,
        centerY: drawingStartPoint.y,
        radius,
      });
      break;
    }
    case "freehand": {
      setDrawingShape((prev) => ({
        ...prev,
        points: [...(prev?.points || []), pdfCoords],
      }));
      break;
    }
    default:
      // No action needed for other drawing modes
      break;
  }
};

/**
 * Finish drawing a shape (except polygon)
 */
export const finishDrawing = ({
  isDrawing,
  drawingMode,
  drawingShape,
  findWordsInShape,
  selectedTable,
  currentPage,
  pdfPageDimensions,
  setPendingShapeWords,
  setShowDrawingConfirmation,
  setIsDrawing,
  setDrawingStartPoint,
  setDrawingShape,
}) => {
  if (!isDrawing) return;

  if (drawingMode === "polygon") {
    // Don't finish polygon on mouse up - wait for double click or escape
    return;
  }

  if (drawingShape) {
    // Find words in the completed shape
    const wordsInShape = findWordsInShape(
      drawingShape,
      selectedTable,
      currentPage,
      pdfPageDimensions
    );
    if (wordsInShape.length > 0) {
      setPendingShapeWords(wordsInShape);
      setShowDrawingConfirmation(true);
    }
  }

  setIsDrawing(false);
  setDrawingStartPoint(null);
  setDrawingShape(null);
};

/**
 * Finish drawing a polygon (called on double-click)
 */
export const finishPolygon = ({
  drawingMode,
  polygonPoints,
  findWordsInShape,
  selectedTable,
  currentPage,
  pdfPageDimensions,
  setPendingShapeWords,
  setShowDrawingConfirmation,
  setPolygonPoints,
  setIsDrawing,
}) => {
  if (drawingMode === "polygon" && polygonPoints.length >= 3) {
    const polygonShape = {
      type: "polygon",
      points: polygonPoints,
    };

    const wordsInShape = findWordsInShape(
      polygonShape,
      selectedTable,
      currentPage,
      pdfPageDimensions
    );
    if (wordsInShape.length > 0) {
      setPendingShapeWords(wordsInShape);
      setShowDrawingConfirmation(true);
    }

    setPolygonPoints([]);
    setIsDrawing(false);
  }
};

/**
 * Cancel current drawing operation
 */
export const cancelDrawing = ({
  setIsDrawing,
  setDrawingStartPoint,
  setDrawingShape,
  setPolygonPoints,
  setDrawingMode,
  setPreviewWords,
}) => {
  setIsDrawing(false);
  setDrawingStartPoint(null);
  setDrawingShape(null);
  setPolygonPoints([]);
  setDrawingMode(null);
  setPreviewWords([]);
};

/**
 * Confirm drawing operation and move selected words to target cell
 */
export const confirmDrawing = ({
  pendingShapeWords,
  selectedTable,
  tableData,
  setTableData,
  setSelectedTable,
  setShowDrawingConfirmation,
  setPendingShapeWords,
  setDrawingMode,
}) => {
  if (pendingShapeWords.length === 0) return;

  // Group selected words by cell with their indices
  const cellSelectedWords = {};
  pendingShapeWords.forEach(({ cellKey, wordIndex, word }) => {
    if (!cellSelectedWords[cellKey]) {
      cellSelectedWords[cellKey] = [];
    }
    cellSelectedWords[cellKey].push({ wordIndex, word });
  });

  // Combine all selected text for the target cell
  const combinedText = pendingShapeWords
    .map(({ word }) => word.text)
    .join(" ")
    .trim();

  // Choose target cell (first cell with selected words)
  const cellKeys = Object.keys(cellSelectedWords);
  if (cellKeys.length > 0 && selectedTable && tableData) {
    const targetCellKey = cellKeys[0];

    const updatedData = { ...tableData };

    // Create updated table structure with modified OCR data
    const updatedTable = { ...selectedTable };

    // Update each affected cell by removing only the selected words
    cellKeys.forEach((cellKey) => {
      const [rowStr, colStr] = cellKey.split("_");
      const rowIndex = parseInt(rowStr);
      const colIndex = parseInt(colStr);
      const selectedWordsInCell = cellSelectedWords[cellKey];

      // Get all words from this cell
      const cellLocationData = selectedTable[cellKey];
      if (
        cellLocationData &&
        Array.isArray(cellLocationData) &&
        cellLocationData[0]?.words
      ) {
        const allWordsInCell = cellLocationData[0].words;

        // Create set of selected word indices for quick lookup
        const selectedIndices = new Set(
          selectedWordsInCell.map((sw) => sw.wordIndex)
        );

        // Keep only non-selected word objects (for OCR data)
        const remainingWordObjects = allWordsInCell.filter(
          (_, index) => !selectedIndices.has(index)
        );

        // Keep only non-selected word text (for display)
        const remainingWords = remainingWordObjects.map((word) => word.text);
        const remainingText = remainingWords.join(" ").trim();

        // Update the display data
        if (cellKey === targetCellKey) {
          // Target cell gets the combined text from all selected words
          if (rowIndex === 0) {
            updatedData.headers[colIndex] = combinedText;
          } else {
            if (!updatedData.rows[rowIndex - 1]) {
              updatedData.rows[rowIndex - 1] = [];
            }
            updatedData.rows[rowIndex - 1][colIndex] = combinedText;
          }

          // Update OCR data: Preserve individual selected words with their original bounding boxes
          const targetCellLocationData = cellLocationData[0];

          // Get all selected word objects from ALL cells (not just current cell)
          const allSelectedWordObjects = pendingShapeWords.map(
            ({ word }) => word
          );

          // Update OCR data with all selected words plus any remaining words from target cell
          updatedTable[cellKey] = [
            {
              ...targetCellLocationData,
              words: [...allSelectedWordObjects, ...remainingWordObjects],
            },
          ];
        } else {
          // Source cells get their remaining text (with selected words removed)
          if (rowIndex === 0) {
            updatedData.headers[colIndex] = remainingText;
          } else {
            if (!updatedData.rows[rowIndex - 1]) {
              updatedData.rows[rowIndex - 1] = [];
            }
            updatedData.rows[rowIndex - 1][colIndex] = remainingText;
          }

          // Update OCR data: Keep only non-selected words
          updatedTable[cellKey] = [
            {
              ...cellLocationData[0],
              words: remainingWordObjects,
            },
          ];
        }
      }
    });

    setTableData(updatedData);
    setSelectedTable(updatedTable);
  }

  // Clean up
  setShowDrawingConfirmation(false);
  setPendingShapeWords([]);
  setDrawingMode(null);
};
